/*  JS of Product Grid Item
    Added by Wilson from Galadrim  */

const whenCustomCartReady = cb => {
  if (window.Shopify?.custom?.cart?.update) return cb();
  setTimeout(() => whenCustomCartReady(cb), 50);
};

/* --------------------------------------------------------------------------
   1.  Event delegation on grid / compare                                       
   -------------------------------------------------------------------------- */
/**
 * Main delegated click handler for quantity buttons and ATC buttons that live
 * in either the product-grid cards or product-compare cards.
 */
function handleProductGridOrCompareClick(e) {
  // --- Handle +/- Clicks
  const qtyBtn = e.target.closest(".qty-btn");
  if (qtyBtn) {
    e.preventDefault();
    const selector = qtyBtn.closest(".quantity-selector");
    const input = selector.querySelector(".qty-input");
    const card = qtyBtn.closest(".productgrid--item, .product-compare__product-card");
    if (!input || !card) return;

    const min = parseInt(input.getAttribute("min"), 10) || 1;
    const current = parseInt(input.value, 10) || min;

    let newValue = current;
    if (qtyBtn.hasAttribute("data-quantity-minus")) {
      newValue = Math.max(min, current - 1);
    }
    if (qtyBtn.hasAttribute("data-quantity-plus")) {
      newValue = current + 1;
    }
    input.value = newValue;
    refreshCardState(card);
    return;
  }

  // --- Handle Add to Cart Button Click
  const atcBtn = e.target.closest('button[type="submit"].productitem--action-atc');
  if (atcBtn) {
    e.preventDefault();
    e.stopPropagation();

    const atcForm = atcBtn.closest("form.productitem--actions");
    const input = atcForm.querySelector(".qty-input");
    const variantId = atcBtn.getAttribute("data-variant-id");
    const inputQuantity = parseInt(input.value, 10) || 1;

    atcBtn.disabled = true;
    document.body.classList.add("processing");

    (async () => {
      try {
        // Check if product is already in cart
        const productCard = atcBtn.closest("[data-product-item]");
        const cartItemKey = productCard?.getAttribute("data-cartitem-key");
        const existingQuantity = parseInt(productCard?.getAttribute("data-cartitem-quantity") || "0", 10);

        let response;
        if (cartItemKey && existingQuantity > 0) {
          // Product is already in cart, use update endpoint with input quantity as new total
          const formData = new FormData();
          formData.append("updates[" + cartItemKey + "]", inputQuantity);

          response = await fetch("/cart/update.js", {
            method: "POST",
            body: formData,
          });
        } else {
          // Product not in cart, use add endpoint
          const formData = new FormData();
          formData.append("id", variantId);
          formData.append("quantity", inputQuantity);

          response = await fetch("/cart/add.js", {
            method: "POST",
            body: formData,
          });
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status} from cart API`);
        }

        const data = await response.json();

        // Get updated cart data
        const cartData = Shopify.cart;

        // Update header cart badge using Empire theme pattern
        const countEvent = new CustomEvent("cartcount:update", {
          detail: cartData,
        });
        window.dispatchEvent(countEvent);

        // Show success feedback
        document.body.classList.remove("processing");
        atcBtn.classList.remove("processing");
        atcBtn.classList.add("added-success");

        setTimeout(() => {
          atcBtn.classList.remove("added-success");
          atcBtn.disabled = false;
          refreshCardState(atcBtn.closest(".productgrid--item, .product-compare__product-card"));
        }, 1200);

        // Show success toast if available
        if (typeof window.showSuccessToast === "function") {
          window.showSuccessToast();
        }

        // Refresh cart drawer if available
        if (window.Shopify?.theme?.cartDrawer) {
          await window.Shopify.theme.cartDrawer.refresh();
          window.Shopify.theme.cartDrawer.open();
        } else {
          document.dispatchEvent(new CustomEvent("cart-drawer:refresh"));
          document.dispatchEvent(new CustomEvent("cart-drawer:open"));
        }

        // Refresh promotions
        document.dispatchEvent(new CustomEvent("promos:refresh"));

        // If on the cart page, refresh the main section as well
        if (document.body.classList.contains("template-cart") && typeof window.refreshCartPageSection === "function") {
          window.refreshCartPageSection();
        }
      } catch (error) {
        console.error("[Product Grid] Cart update error:", error);
        atcBtn.disabled = false;
        atcBtn.classList.remove("processing");
      }
    })();
  }
}

/* Live “clamp + disable” on manual quantity edits ------------------------- */
document.addEventListener(
  "input",
  e => {
    if (e.target.matches(".productgrid--item .qty-input, .product-compare__product-card .qty-input")) {
      clampGridInput(e.target); // keeps value <= limit
    }
  },
  true
); // capture-phase = runs before theme handlers

/* --------------------------------------------------------------------------
     2.  Helpers                                                                
     -------------------------------------------------------------------------- */

/**
 * Enable / disable ATC button and plus button according to the quantity and
 * the effective limit for the product.
 */
function refreshCardState(card) {
  const atc = card.querySelector(".productitem--action-atc");
  const qtyI = card.querySelector(".qty-input");
  const plus = card.querySelector(".qty-plus");
  const minus = card.querySelector(".qty-minus");
  if (!atc || !qtyI) return;

  const qty = parseInt(qtyI.value, 10) || 1;
  const limit = window.getEffectiveLimit ? window.getEffectiveLimit(atc) : 0;

  /* ATC state ------------------------------------------------------------- */
  if (limit > 0 && qty >= limit + 1) {
    atc.classList.add("disabled", "limit-disabled");
    atc.setAttribute("disabled", "disabled");
  } else {
    atc.classList.remove("disabled", "limit-disabled");
    atc.removeAttribute("disabled");
  }

  /* + button state -------------------------------------------------------- */
  if (plus) {
    if (limit > 0 && qty >= limit + 1) plus.setAttribute("disabled", "disabled");
    else plus.removeAttribute("disabled");
  }

  /* - button state -------------------------------------------------------- */
  if (minus) {
    if (qty <= 1) {
      minus.disabled = true;
      minus.classList.add("disabled");
    } else {
      minus.disabled = false;
      minus.classList.remove("disabled");
    }
  }
}

/**
 * Clamp manual input inside grid / compare cards and re-evaluate button state.
 */
function clampGridInput(input) {
  const card = input.closest(".productgrid--item, .product-compare__product-card");
  const atc = card?.querySelector(".productitem--action-atc");
  if (!card || !atc) return;

  const limit = window.getEffectiveLimit ? window.getEffectiveLimit(atc) : 0;
  /* accept one unit over the hard limit, clamp anything above that       */
  if (limit > 0 && parseInt(input.value, 10) > limit + 1) {
    input.value = limit + 1;
  }

  refreshCardState(card);
}

/* --------------------------------------------------------------------------
     3.  Wiring                                                                
     -------------------------------------------------------------------------- */

function setupProductGridQuantityAndCart() {
  const grid =
    document.querySelector(".productgrid--outer") ||
    document.querySelector(".wishlist .products") ||
    document.querySelector(".featured-collection__container");
  if (grid && !grid.hasAttribute("data-grid-handler")) {
    grid.setAttribute("data-grid-handler", "true");
    grid.addEventListener("click", handleProductGridOrCompareClick, true);
  }
}

function setupProductCompareQuantityAndCart() {
  const table = document.querySelector(".product-compare__table");
  if (table && !table.hasAttribute("data-compare-handler")) {
    table.setAttribute("data-compare-handler", "true");
    table.addEventListener("click", handleProductGridOrCompareClick, true);
  }
}

/* Live input clamping ----------------------------------------------------- */
document.addEventListener(
  "input",
  e => {
    if (e.target.matches(".productgrid--item .qty-input, .product-compare__product-card .qty-input")) {
      clampGridInput(e.target);
    }
  },
  true
);

/* Initial load ------------------------------------------------------------ */
document.addEventListener("DOMContentLoaded", () => {
  setupProductGridQuantityAndCart();
  setupProductCompareQuantityAndCart();
  initializeGridAndCompareState(); // first pass to sync limits
});

/* Section (re)loads ------------------------------------------------------- */
document.addEventListener("shopify:section:load", evt => {
  if (evt.target.closest(".productgrid--outer")) setupProductGridQuantityAndCart();
  if (evt.target.querySelector(".product-compare__table")) setupProductCompareQuantityAndCart();
  initializeGridAndCompareState(); // re-sync after snippet reload
});

/* --------------------------------------------------------------------------
     4.  Grid / compare initial scan
     -------------------------------------------------------------------------- */

function initializeGridAndCompareState() {
  document
    .querySelectorAll(".productgrid--item, .product-compare__product-card")
    .forEach(card => refreshCardState(card));
}

/* --------------------------------------------------------------------------
    5.  Compate toggle & view (grid/list) toggle
    -------------------------------------------------------------------------- */

// Compare Toggle / Product Grid Begin
// Immediately hide all compare wrappers on script load
if (document.body.classList.contains("template-search") || document.body.classList.contains("template-collection")) {
  document.querySelectorAll(".productitem__compare-wrapper").forEach(function (wrapper) {
    wrapper.style.display = "none";
    wrapper.style.height = "0";
    wrapper.style.opacity = "0";
    wrapper.removeAttribute("data-animation-state");
  });
}

// Compare toggle functions
function updateCompareWrappers(show) {
  document.querySelectorAll(".productitem__compare-wrapper").forEach(function (wrapper) {
    if (show) {
      wrapper.setAttribute("data-animation-state", "open");
      wrapper.style.setProperty("display", "flex", "important");
      wrapper.style.setProperty("opacity", "1", "important");
      wrapper.style.setProperty("height", "auto", "important");
    } else {
      wrapper.removeAttribute("data-animation-state");
      wrapper.style.setProperty("display", "none", "important");
      wrapper.style.setProperty("opacity", "0", "important");
      wrapper.style.setProperty("height", "0", "important");
    }
  });
}

function getViewFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get("grid_list") || localStorage.getItem("view") || "grid-view";
}

function setView(view) {
  const gridContainer = document.querySelector(".productgrid--outer");
  const viewToggleButtons = document.querySelectorAll("[data-collection-view]");

  // Remove both classes first
  gridContainer.classList.remove("productgrid-gridview", "productgrid-listview");
  // Add the correct class
  gridContainer.classList.add("productgrid-" + view.replace("-", ""));
  // Update button active state
  viewToggleButtons.forEach(function (btn) {
    btn.classList.toggle("active", btn.getAttribute("data-collection-view") === view);
  });
}

document.addEventListener("DOMContentLoaded", function () {
  if (document.body.classList.contains("template-search") || document.body.classList.contains("template-collection")) {
    const compareToggle = document.querySelector("[data-compare-toggle]");
    const compareWrappers = document.querySelectorAll(".productitem__compare-wrapper");

    if (compareToggle) {
      compareToggle.checked = false;
    }
    updateCompareWrappers(false);

    if (compareToggle) {
      compareToggle.addEventListener("change", function () {
        updateCompareWrappers(compareToggle.checked);
        document.body.classList.toggle("compare-enabled", compareToggle.checked);
      });
    }
  }
});

// View Toggle / Grid / List
document.addEventListener("DOMContentLoaded", function () {
  if (document.body.classList.contains("template-search")) {
    // --- View Toggle ---
    const gridContainer = document.querySelector(".productgrid--outer");
    const viewToggleButtons = document.querySelectorAll("[data-collection-view]");

    // On page load, set the view
    setView(getViewFromUrl());

    // On click, update URL and reload
    viewToggleButtons.forEach(function (btn) {
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        const view = btn.getAttribute("data-collection-view");
        localStorage.setItem("view", view);
        const params = new URLSearchParams(window.location.search);
        params.set("grid_list", view);
        window.location.search = params.toString();
      });
    });
  }
});

// Force compare wrapper visibility in collections
function forceCollectionCompareWrapperState() {
  if (document.body.classList.contains("template-collection")) {
    const compareToggle = document.querySelector("[data-compare-toggle]");
    const show = compareToggle && compareToggle.checked;

    // Update body class for CSS targeting
    document.body.classList.toggle("compare-enabled", show);

    // Force update wrapper visibility
    document.querySelectorAll(".productitem__compare-wrapper").forEach(function (wrapper) {
      if (show) {
        wrapper.setAttribute("data-animation-state", "open");
        wrapper.style.setProperty("display", "flex", "important");
        wrapper.style.setProperty("opacity", "1", "important");
        wrapper.style.setProperty("height", "auto", "important");
      } else {
        wrapper.removeAttribute("data-animation-state");
        wrapper.style.setProperty("display", "none", "important");
        wrapper.style.setProperty("opacity", "0", "important");
        wrapper.style.setProperty("height", "0", "important");
      }
    });

    // Force update label visibility
    document.querySelectorAll(".productitem__compare-checkbox-label").forEach(function (label) {
      label.style.setProperty("display", show ? "flex" : "none", "important");
    });
  }
}
document.addEventListener("DOMContentLoaded", forceCollectionCompareWrapperState);
document.addEventListener("shopify:section:load", forceCollectionCompareWrapperState);
if (document.body.classList.contains("template-collection")) {
  const compareToggle = document.querySelector("[data-compare-toggle]");
  if (compareToggle) {
    compareToggle.addEventListener("change", forceCollectionCompareWrapperState);
  }
}
